import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { UserContext } from '../context/UserContext';
import { useQuoteStore, useQuoteStoreBase, type Quote } from '../stores/quoteStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, RowActions, RowAction, StatusDot, Amount, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton, btnPrimary, btnDisabled } from '@redpanda/ui';
import { quoteTone, toneOf } from '../utils/statusTone';
import QuoteDialog from '../components/QuoteDialog';
import CustomerLimitBanner from '../components/CustomerLimitBanner';
import { useCustomerLimitCache } from '../hooks/useCustomerLimitCache';
import { bulkDeleteQuotes, getQuotes } from '../services/api';
import { useBulkActions, isAdmin } from '@redpanda/utils';
import { canModify } from '../utils/permissions';
import { notifyDataChanged } from '../utils/dataEvents';
import { useListSearch } from '../hooks/useListSearch';
import { pendingRowClass } from '../utils/pendingItem';
import { createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler } from '@redpanda/utils';
import { TableSkeleton } from '@redpanda/ui';
import { getCustomerStatus, getArchivedRowClassName } from '../utils/customerUtils';


// 已成交的報價可以在 PM 一鍵建立專案（客戶、金額、項目帶入；PM 端以 CRM 資料為準）；建立過的改成「查看 PM 專案」
const WON_QUOTE_STATUSES = ['ACCEPTED', 'COMPLETED'];
const PM_FRONTEND_URL = (process.env.REACT_APP_PM_FRONTEND_URL || 'http://127.0.0.1:8005').replace(/\/$/, '');

const QuoteListPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  
  // 使用客戶限制快取 hook
  const {
    canAddCustomer,
    setCanAddCustomer,
    limitBannerRefreshTrigger,
  } = useCustomerLimitCache();

  const {
    quotes,
    pagination,
    error,
    isPageLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    fetchQuotes,
  } = useQuoteStore();

  // 搜尋由後端查全部資料；沒有搜尋時用列表 store
  const [searchTerm, setSearchTerm] = useState('');
  const search = searchTerm.trim();
  const searchResult = useListSearch<Quote>('quotes', search ? { search } : null, page => getQuotes(page, { search }));
  const listItems = searchResult.active ? searchResult.items : quotes;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useQuoteStoreBase.getState(), fetchQuotes, setCurrentPage);

  // 使用新的批次操作 hook
  const {
    selectedItems: selectedQuotes,
    showBulkDeleteModal,
    isBulkDeleting,
    isItemSelected,
    allSelected,
    toggleItemSelection,
    toggleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    closeBulkDeleteModal,
    canDelete,
    handlePageChange
  } = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('quotes.item', { ns: 'pages' }),
    bulkDeleteApi: bulkDeleteQuotes,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useQuoteStoreBase.setState,
      'quote',
      () => quotes,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('quotes');
    },
    userRole,
    t,
    toast,
    // 智能分頁配置
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages
    } : undefined,
    fetchItems: fetchQuotes,
    setCurrentPage
  });

  // 使用 ref 來穩定化函數引用，避免無限循環
  const fetchQuotesRef = useRef(fetchQuotes);
  fetchQuotesRef.current = fetchQuotes;

  useEffect(() => {
    if (isAuthenticated && currentUserId) {
      // 頁面初始化時使用快取（如果有），避免每次切換都重新載入
      fetchQuotesRef.current(1, false);
    }
  }, [isAuthenticated, currentUserId]);

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : handlePageChange(page));

  const handleAddQuote = () => {
    setSelectedQuote(null);
    setIsDialogOpen(true);
  };

  const handleEditQuote = (quote: any) => {
    setSelectedQuote(quote);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedQuote(null);
  };


  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated || !currentUserId) {
    return <div className="p-8 text-center text-red-500">{t('common.messages.unauthorized')}</div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('quotes.list.title', { ns: 'pages' })}</h1>
          <div className="flex space-x-3">
            {/* 批量操作按鈕 */}
            {isAdmin(userRole) && (
              <MultiBulkActionButton
                selectedCount={selectedQuotes.length}
                isDeleting={isBulkDeleting}
                onDelete={handleBulkDelete}
              />
            )}
            <button 
              onClick={() => canAddCustomer && handleAddQuote()}
              disabled={!canAddCustomer}
              className={canAddCustomer ? btnPrimary : btnDisabled}
              title={canAddCustomer ? undefined : t('customers.list.limitReached', { ns: 'pages' })}
            >
              {t('quotes.list.addButton', { ns: 'pages' })}
            </button>
          </div>
        </div>
      </div>

      {/* 客戶數量限制資訊 */}
      <CustomerLimitBanner 
        onLimitChange={setCanAddCustomer} 
        refreshTrigger={limitBannerRefreshTrigger}
      />

      {error && (
        <div className="mb-4 p-4 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('quotes.list.searchPlaceholder', { ns: 'pages' })} />
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {/* 骨架屏載入狀態 */}
      {listLoading ? (
        <TableSkeleton rows={10} columns={6} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
            <TableHeader>
              <TableRow>
                <BulkSelectHeader
                  userRole={userRole}
                  allSelected={allSelected}
                  onToggleAll={toggleSelectAll}
                  hasItems={listItems.length > 0}
                />
                <TableHead className="w-56">{t('common.table.columns.title')}</TableHead>
                <TableHead className="w-48">{t('common.table.columns.customer')}</TableHead>
                <TableHead numeric className="w-36">{t('common.table.columns.totalAmount')}</TableHead>
                <TableHead className="w-32 pl-10">{t('common.table.columns.status')}</TableHead>
                <TableHead className="w-32">{t('common.table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listItems.length === 0 ? (
                <ListEmptyRow
                  colSpan={6}
                  filtered={!!search}
                  title={t('quotes.list.noQuotes', { ns: 'pages' })}
                  hint={t('quotes.list.emptyHint', { ns: 'pages' })}
                  action={canAddCustomer && (
                    <button type="button" onClick={handleAddQuote} className={btnPrimary}>{t('quotes.list.addButton', { ns: 'pages' })}</button>
                  )}
                />
              ) : (
                listItems.map((quote) => (
                  <TableRow key={quote.id} className={`${getArchivedRowClassName(quote.customer)} ${pendingRowClass(quote)}`}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={isItemSelected(quote.id)}
                      onToggle={() => toggleItemSelection(quote.id)}
                      canDelete={canDelete()}
                    />
                    <TableCell className="font-medium truncate">{quote.title}</TableCell>
                    <TableCell className="truncate">
                      {quote.customer?.name || <span className="text-fg-muted">—</span>}
                      {getCustomerStatus(quote.customer).isArchived && (
                        <span className="ml-1 text-[13px] text-fg-secondary">({t('common.archived')})</span>
                      )}
                    </TableCell>
                    <TableCell numeric><Amount value={quote.totalAmount} /></TableCell>
                    <TableCell className="pl-10">
                      <StatusDot
                        tone={toneOf(quoteTone, quote.status)}
                        label={['DRAFT', 'SENT', 'ACCEPTED', 'REJECTED', 'COMPLETED'].includes(quote.status)
                          ? t(`quotes.status.${quote.status.toLowerCase()}`, { ns: 'pages' })
                          : quote.status}
                      />
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        {quote.pmProjectId ? (
                          <RowAction onClick={() => window.open(`${PM_FRONTEND_URL}/projects/${encodeURIComponent(quote.pmProjectId!)}`, '_blank', 'noopener')}>
                            {t('quotes.viewPmProject', { ns: 'pages' })}
                          </RowAction>
                        ) : WON_QUOTE_STATUSES.includes(quote.status) && (
                          <RowAction onClick={() => window.open(`${PM_FRONTEND_URL}/projects?fromQuote=${encodeURIComponent(quote.id)}`, '_blank', 'noopener')}>
                            {t('quotes.createPmProject', { ns: 'pages' })}
                          </RowAction>
                        )}
                        {canModify(quote, userRole, currentUserId) && (
                          <>
                            <RowAction onClick={() => handleEditQuote(quote)}>{t('common.actions.edit')}</RowAction>
                            <RowAction tone="danger" onClick={() => handleDelete(quote.id)}>{t('common.actions.delete')}</RowAction>
                          </>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('quotes.list.confirmDeleteTitle', { ns: 'pages' })}
        message={t('quotes.list.confirmDeleteMessage', { ns: 'pages' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <QuoteDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        quote={selectedQuote}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('common.bulk.confirmDeleteTitle', { count: selectedQuotes.length, entity: t('quotes.item', { ns: 'pages' }) })}
        message={t('common.bulk.confirmDeleteMessage', { count: selectedQuotes.length, entity: t('quotes.item', { ns: 'pages' }) })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default QuoteListPage;